import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import { useEffect } from 'react';
import useWrapperCookie from './useWrapperCookie.js';
import { WRAPPER_COOKIE_NAME, TEMPORARY_CONSENT_BASE_KEY } from '../utils.js';
import { useSessionStorageObserver } from './useSessionStorage.js';
import SingletonFetch from './fetchPluginData.js';

function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }

// Function to inject raw script content or external script
function injectRawScript(scriptContent, id) {
  var tempDiv = document.createElement("div");
  tempDiv.innerHTML = scriptContent.trim();
  var parsedScript = tempDiv.querySelector("script");
  if (!parsedScript) {
    console.error("No <script> tag found in the provided content for ID ".concat(id));
    return null;
  }
  if (parsedScript.src) {
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.id = id;
    script.src = parsedScript.src;
    script.onerror = function () {
      console.error("Failed to load external script with ID ".concat(id, " from ").concat(parsedScript.src));
    };
    document.head.appendChild(script);
    return script;
  } else {
    // Inline script content
    var _script = document.createElement("script");
    _script.type = "text/javascript";
    _script.async = true;
    _script.id = id;
    _script.textContent = parsedScript.textContent;
    document.head.appendChild(_script);
    return _script;
  }
}

// Function to inject noscript content
function injectNoScript(noscriptContent, id) {
  var noscript = document.createElement("noscript");
  noscript.id = id;
  // Directly assign the noscript content without adding extra quotes
  noscript.innerHTML = noscriptContent.trim(); // Make sure there are no accidental spaces
  document.body.appendChild(noscript); // Append the noscript to the body
  return noscript;
}

// Function to parse script content with multiple scripts and a noscript block
function parseScriptContent(scriptData, id) {
  var scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
  var noscriptRegex = /<noscript[^>]*>([\s\S]*?)<\/noscript>/i;
  var match;
  var index = 0;
  while ((match = scriptRegex.exec(scriptData)) !== null) {
    var fullScriptTag = match[0]; // The full <script>...</script> tag
    var scriptId = "".concat(id, "-script-").concat(index);
    injectRawScript(fullScriptTag, scriptId);
    index++;
  }
  var noscriptMatch = noscriptRegex.exec(scriptData);
  if (noscriptMatch) {
    injectNoScript(noscriptMatch[1], "".concat(id, "-noscript"));
  }
}
var useScriptInjection = function useScriptInjection(wpApiUrl) {
  var path = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var prevent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var _useWrapperCookie = useWrapperCookie(WRAPPER_COOKIE_NAME),
    actualCookie = _useWrapperCookie.actualCookie;
  if (!actualCookie || actualCookie.length < 1) return;
  var changeSwitch = useSessionStorageObserver();
  var injectScripts = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _data$acf, data, options, _iterator, _step, cookieGroup, _iterator2, _step2, _loop;
      return _regeneratorRuntime.wrap(function _callee$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return SingletonFetch.fetchPluginData(wpApiUrl, undefined, path);
          case 3:
            data = _context2.sent;
            options = (_data$acf = data.acf) === null || _data$acf === void 0 ? void 0 : _data$acf.consent_plugin_data;
            if (!(options !== null && options !== void 0 && options.cookies_groups)) {
              _context2.next = 39;
              break;
            }
            _iterator = _createForOfIteratorHelper(options.cookies_groups);
            _context2.prev = 7;
            _iterator.s();
          case 9:
            if ((_step = _iterator.n()).done) {
              _context2.next = 31;
              break;
            }
            cookieGroup = _step.value;
            if (Array.isArray(cookieGroup.consent_plugin_cookies)) {
              _context2.next = 13;
              break;
            }
            return _context2.abrupt("continue", 29);
          case 13:
            _iterator2 = _createForOfIteratorHelper(cookieGroup.consent_plugin_cookies);
            _context2.prev = 14;
            _loop = /*#__PURE__*/_regeneratorRuntime.mark(function _loop() {
              var cookieDetails, details, consentGiven, existingScripts;
              return _regeneratorRuntime.wrap(function _loop$(_context) {
                while (1) switch (_context.prev = _context.next) {
                  case 0:
                    cookieDetails = _step2.value;
                    details = JSON.parse(cookieDetails.cookie_list);
                    consentGiven = actualCookie[details.name.toLowerCase()] || JSON.parse(sessionStorage.getItem("".concat(TEMPORARY_CONSENT_BASE_KEY, "-").concat(details.name.toLowerCase())) || "false");
                    existingScripts = document.querySelectorAll("[id*=\"".concat(details.name.toLowerCase(), "-injected-script\"]"));
                    if (consentGiven && existingScripts.length === 0 && !prevent) {
                      if (Array.isArray(cookieDetails.script)) {
                        cookieDetails.script.forEach(function (scriptInfo, index) {
                          if (scriptInfo.domain && scriptInfo.domain.length > 0 && scriptInfo.domain !== window.location.hostname) {
                            return;
                          }
                          var scriptId = "".concat(details.name.toLowerCase(), "-injected-script-").concat(index);
                          parseScriptContent(scriptInfo.script, scriptId);
                        });
                      }
                    } else if ((!consentGiven || prevent) && (existingScripts === null || existingScripts === void 0 ? void 0 : existingScripts.length) > 0) {
                      existingScripts === null || existingScripts === void 0 || existingScripts.forEach(function (oldScript) {
                        try {
                          document.head.removeChild(oldScript);
                        } catch (error) {
                          // Fail silently
                        }
                      });
                    }
                  case 5:
                  case "end":
                    return _context.stop();
                }
              }, _loop);
            });
            _iterator2.s();
          case 17:
            if ((_step2 = _iterator2.n()).done) {
              _context2.next = 21;
              break;
            }
            return _context2.delegateYield(_loop(), "t0", 19);
          case 19:
            _context2.next = 17;
            break;
          case 21:
            _context2.next = 26;
            break;
          case 23:
            _context2.prev = 23;
            _context2.t1 = _context2["catch"](14);
            _iterator2.e(_context2.t1);
          case 26:
            _context2.prev = 26;
            _iterator2.f();
            return _context2.finish(26);
          case 29:
            _context2.next = 9;
            break;
          case 31:
            _context2.next = 36;
            break;
          case 33:
            _context2.prev = 33;
            _context2.t2 = _context2["catch"](7);
            _iterator.e(_context2.t2);
          case 36:
            _context2.prev = 36;
            _iterator.f();
            return _context2.finish(36);
          case 39:
            _context2.next = 44;
            break;
          case 41:
            _context2.prev = 41;
            _context2.t3 = _context2["catch"](0);
            console.error("Error injecting scripts:", _context2.t3);
          case 44:
          case "end":
            return _context2.stop();
        }
      }, _callee, null, [[0, 41], [7, 33, 36, 39], [14, 23, 26, 29]]);
    }));
    return function injectScripts() {
      return _ref.apply(this, arguments);
    };
  }();
  useEffect(function () {
    injectScripts()["catch"](function (error) {
      console.error(error);
    });
  }, [actualCookie, changeSwitch, prevent]);
};

export { useScriptInjection };
//# sourceMappingURL=useScriptInjection.js.map
